// @file Surface post action store
// TODO: [to-be-migrated][post_action]
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import type { Cid, Id, Post, PostAttributes, WallId } from '@@/types'
import { OzConfirmationDialogBoxButtonScheme } from '@@/vuexstore/modules/global_confirmation_dialog'
import type { RootState as SurfaceRootState } from '@@/vuexstore/surface/types'
import type { PostColor } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface CopyXferPostParams {
  wallId?: Id
  wallDescription?: string
  wallTitle?: string
  wallSectionId?: Id
  isDestinationThisWall?: boolean
  postPosition?: { left: number; top: number }
  postId: Id
  transferredPostCid?: Cid
  libraryId?: string
}

export const useSurfacePostActionStore = defineStore('postAction', () => {
  const vuexStore = getVuexStore<SurfaceRootState>()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const expandedPostStore = useExpandedPostStore()

  /* ---------------------- */
  /* CURRENTLY PLAYING POST */
  /* ---------------------- */
  const currentlyPlayingPostCid = ref<Cid | null>(null)

  const setCurrentlyPlayingPost = (cid: Cid | null): void => {
    currentlyPlayingPostCid.value = cid
  }

  const unsetCurrentlyPlayingPost = (): void => {
    currentlyPlayingPostCid.value = null
  }

  /* ---------------------- */
  /* ZOOM POST              */
  /* ---------------------- */
  const zoomedPostCid = computed<Cid | null>(() => vuexStore?.getters['postAction/zoomedPostCid'])
  const zoomedLink = computed<string | null>(() => vuexStore?.getters['postAction/zoomedLink'])
  const zoomPost = (payload: { zoomedPostCid: Cid; zoomedLink: string | null }): void => {
    void vuexStore?.dispatch('postAction/zoomPost', payload)
  }

  const unzoomPost = (): void => {
    void vuexStore?.dispatch('postAction/unzoomPost')
  }

  /* ---------------------- */
  /* POLL                   */
  /* ---------------------- */
  const doesPostHavePoll = (post: PostAttributes): boolean => {
    return vuexStore?.getters['postAction/doesPostHavePoll'](post)
  }

  const getPoll = (post: PostAttributes): boolean => {
    return vuexStore?.getters['postAction/getPoll'](post)
  }

  const isPollClosed = (post: PostAttributes): boolean => {
    return vuexStore?.getters['postAction/isPollClosed'](post)
  }

  const closePoll = (payload: { post: Post }): void => {
    void vuexStore?.dispatch('postAction/closePoll', payload)
  }

  const reopenPoll = (payload: { post: Post }): void => {
    void vuexStore?.dispatch('postAction/reopenPoll', payload)
  }

  /* ---------------------- */
  /* POST UNDER ACTION      */
  /* ---------------------- */
  const postUnderAction = computed<Post | undefined>(() => vuexStore?.getters['postAction/postUnderAction'])
  const postUnderActionCid = computed<Cid | null>(() => vuexStore?.getters['postAction/postUnderActionCid'])
  const xPostAction = computed<boolean>(() => vuexStore?.getters['postAction/xPostAction'])

  const postRightClickCoordinates = computed<[number, number] | null>(
    () => vuexStore?.getters['postAction/postRightClickCoordinates'],
  )

  const hasPostUnderActionWithConnections = computed<boolean>(
    () => vuexStore?.getters['postAction/hasPostUnderActionWithConnections'],
  )
  const isPostUnderActionUserAuthored = computed<boolean>(
    () => vuexStore?.getters['postAction/isPostUnderActionUserAuthored'],
  )
  const doesPostUnderActionHaveAttachment = computed<boolean>(
    () => vuexStore?.getters['postAction/doesPostUnderActionHaveAttachment'],
  )

  const isPostUnderActionPreviewPost = computed<boolean>(
    () => vuexStore?.getters['postAction/isPostUnderActionPreviewPost'],
  )

  const showPostActionMenu = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('postAction/showPostActionMenu', payload)
  }
  const hidePostActionMenu = (): void => {
    void vuexStore?.dispatch('postAction/hidePostActionMenu')
  }

  const setPostRightClickCoordinates = (payload: { postRightClickCoordinates: [number, number] | null }): void => {
    void vuexStore?.dispatch('postAction/setPostRightClickCoordinates', payload)
  }

  /* --------------------------------------- */
  /* POST ACTIONS THAT CAN BE PERFORMED      */
  /* --------------------------------------- */
  const canEditPostUnderAction = computed<boolean>(() => vuexStore?.getters['postAction/canEditPostUnderAction'])
  const canDeletePostUnderAction = computed<boolean>(() => vuexStore?.getters['postAction/canDeletePostUnderAction'])
  const canTransferPostUnderAction = computed<boolean>(
    () => vuexStore?.getters['postAction/canTransferPostUnderAction'],
  )
  const canCopyPostUnderAction = computed<boolean>(() => vuexStore?.getters['postAction/canCopyPostUnderAction'])
  const canOpenInMapApp = computed<boolean>(() => vuexStore?.getters['postAction/canOpenInMapApp'])
  const canConnectPostUnderAction = computed<boolean>(() => vuexStore?.getters['postAction/canConnectPostUnderAction'])
  const canDisconnectPostUnderAction = computed<boolean>(
    () => vuexStore?.getters['postAction/canDisconnectPostUnderAction'],
  )
  const canChangeCoverToPostUnderAction = computed<boolean>(
    () => vuexStore?.getters['postAction/canChangeCoverToPostUnderAction'],
  )
  const canChangePostUnderActionLocation = computed<boolean>(
    () => vuexStore?.getters['postAction/canChangePostUnderActionLocation'],
  )
  const canChangePostStackOrder = computed<boolean>(() => vuexStore?.getters['postAction/canChangePostStackOrder'])
  const canChangeCoverToExpandedPost = computed<boolean>(
    () => vuexStore?.getters['postAction/canChangeCoverToExpandedPost'],
  )
  /* ------------------------- */
  /* POST ACTIONS - TRANSFER   */
  /* ------------------------- */
  const xTransferPostPanel = computed<boolean>(() => vuexStore?.getters['postAction/xTransferPostPanel'])
  const postBeingTransferredCid = computed<Cid | null>(() => vuexStore?.getters['postAction/postBeingTransferredCid'])
  const transferPostActivePanel = computed<string | null>(
    () => vuexStore?.getters['postAction/transferPostActivePanel'],
  )
  const copyOrTransferPostResult = computed(() => vuexStore?.getters['postAction/copyOrTransferPostResult'])
  const copyOrTransferSectionId = computed<Id | null>(() => vuexStore?.getters['postAction/copyOrTransferSectionId'])

  const startPostTransfer = (payload: { transferPostCid: Cid }): void => {
    void vuexStore?.dispatch('postAction/startPostTransfer', payload)
  }

  const endPostTransfer = (): void => {
    void vuexStore?.dispatch('postAction/endPostTransfer')
  }

  const actuallyTransferPost = (params: CopyXferPostParams): void => {
    void vuexStore?.dispatch('postAction/actuallyTransferPost', params)
  }

  const updatePostTransferred = (payload: {
    transferredPostCid: Cid
    error: any
    newPostData: PostAttributes
    isDestinationNewWall: boolean
    isDestinationThisWall: boolean
  }): void => {
    void vuexStore?.dispatch('postAction/updatePostTransferred', payload)
  }

  const postTransferNavigate = (payload: { panel: string | null }): void => {
    void vuexStore?.dispatch('postAction/postTransferNavigate', payload)
  }

  /* ------------------------- */
  /* POST ACTIONS - COPYING    */
  /* ------------------------- */
  const xCopyPostPanel = computed<boolean>(() => vuexStore?.getters['postAction/xCopyPostPanel'])
  const postBeingCopiedCid = computed<Cid | null>(() => vuexStore?.getters['postAction/postBeingCopiedCid'])
  const copyPostActivePanel = computed<string | null>(() => vuexStore?.getters['postAction/copyPostActivePanel'])

  const startPostCopy = (payload: { copyPostCid: Cid }): void => {
    void vuexStore?.dispatch('postAction/startPostCopy', payload)
  }

  const endPostCopy = (): void => {
    void vuexStore?.dispatch('postAction/endPostCopy')
  }

  const actuallyCopyPost = (params: CopyXferPostParams): void => {
    void vuexStore?.dispatch('postAction/actuallyCopyPost', params)
  }

  const updatePostCopied = (payload: {
    error: any
    newPostData: PostAttributes
    isDestinationNewWall: boolean
  }): void => {
    void vuexStore?.dispatch('postAction/updatePostCopied', payload)
  }

  const addPostCopiedOrMovedToCurrent = (payload: { newPostData: PostAttributes }): void => {
    void vuexStore?.dispatch('postAction/addPostCopiedOrMovedToCurrent', payload)
  }

  const postCopyNavigate = (payload: { panel: string | null }): void => {
    void vuexStore?.dispatch('postAction/postCopyNavigate', payload)
  }

  /* ---------------------------- */
  /* POST ACTIONS - DELETE        */
  /* ---------------------------- */
  const confirmDeletePost = (payload: { postCid: Cid }): void => {
    globalConfirmationDialogStore.openConfirmationDialog({
      title: __('Delete post?'),
      body: __('Are you sure you want to delete this post? This cannot be undone!'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Nevermind'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [
        () => expandedPostStore.unexpandPost(),
        () => vuexStore?.dispatch('post/deletePost', payload, { root: true }),
      ],
    })
  }

  const deletePostRemote = (post: Post): void => {
    void vuexStore?.dispatch('postAction/deletePostRemote', post)
  }

  /* ---------------------- */
  /* POST ACTIONS - GENERAL */
  /* ---------------------- */
  const destinationWallSections = computed<object[]>(() => vuexStore?.getters['postAction/destinationWallSections'])
  const destinationWallSectionsFetched = computed<boolean>(
    () => vuexStore?.getters['postAction/destinationWallSectionsFetched'],
  )
  const fetchDestinationWallSections = (payload: { wallId: WallId }): void => {
    void vuexStore?.dispatch('postAction/fetchDestinationWallSections', payload)
  }

  const confirmModerationRejectPost = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('postAction/confirmModerationRejectPost', payload)
  }

  const copyPostLinkAndAlert = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('postAction/copyPostLinkAndAlert', payload)
  }

  const copyAttachmentLinkAndAlert = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('postAction/copyAttachmentLinkAndAlert', payload)
  }

  const recalibrate = (): void => {
    void vuexStore?.dispatch('postAction/recalibrate')
  }

  const changePostColor = (payload: { postCid: Cid; newBgColor: PostColor }): void => {
    void vuexStore?.dispatch('post/changePostColor', payload)
  }

  const removePostColor = (payload: { postCid: Cid }): void => {
    void vuexStore?.dispatch('post/removePostColor', payload)
  }

  const setCoverPost = (payload: { postId: Id }): void => {
    void vuexStore?.dispatch('setCoverPost', payload)
  }

  const unsetCoverPost = (payload: { postId: Id }): void => {
    void vuexStore?.dispatch('unsetCoverPost', payload)
  }

  return {
    // state
    currentlyPlayingPostCid,

    // getters
    zoomedPostCid,
    zoomedLink,
    postUnderActionCid,
    postRightClickCoordinates,
    xTransferPostPanel,
    postBeingTransferredCid,
    transferPostActivePanel,
    copyOrTransferPostResult,
    xCopyPostPanel,
    postBeingCopiedCid,
    copyPostActivePanel,
    destinationWallSections,
    destinationWallSectionsFetched,
    copyOrTransferSectionId,
    hasPostUnderActionWithConnections,
    xPostAction,
    isPostUnderActionUserAuthored,
    canEditPostUnderAction,
    canDeletePostUnderAction,
    canTransferPostUnderAction,
    canCopyPostUnderAction,
    canOpenInMapApp,
    canConnectPostUnderAction,
    canDisconnectPostUnderAction,
    canChangePostStackOrder,
    doesPostUnderActionHaveAttachment,
    canChangeCoverToPostUnderAction,
    canChangeCoverToExpandedPost,
    canChangePostUnderActionLocation,
    isPostUnderActionPreviewPost,
    postUnderAction,

    // getter functions
    doesPostHavePoll,
    getPoll,
    isPollClosed,

    // actions
    setCurrentlyPlayingPost,
    unsetCurrentlyPlayingPost,
    zoomPost,
    unzoomPost,
    showPostActionMenu,
    setPostRightClickCoordinates,
    hidePostActionMenu,
    startPostTransfer,
    endPostTransfer,
    actuallyTransferPost,
    updatePostTransferred,
    postTransferNavigate,
    startPostCopy,
    endPostCopy,
    actuallyCopyPost,
    updatePostCopied,
    addPostCopiedOrMovedToCurrent,
    postCopyNavigate,
    fetchDestinationWallSections,
    confirmDeletePost,
    confirmModerationRejectPost,
    copyPostLinkAndAlert,
    copyAttachmentLinkAndAlert,
    closePoll,
    reopenPoll,
    deletePostRemote,
    recalibrate,
    changePostColor,
    removePostColor,
    setCoverPost,
    unsetCoverPost,
  }
})
